/**
 * BrandContextSection
 *
 * Reusable brand context section with:
 * - Brand selection (optional locked state)
 * - Brand details (logos, colors, AI description)
 *
 * Used in: Upload, Gallery, Product tabs
 *
 * @accessibility WCAG 2.1 AA compliant
 */

import { useState } from 'react';
import { Lock, ChevronDown, ChevronRight, Palette, Image as ImageIcon, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { BrandSelector } from '@/components/brands/BrandSelector';
import { useBrand, useBrandAssets } from '@/hooks/useBrands';
import { getAssetUrl } from '@/lib/helpers/asset-urls';

interface BrandContextSectionProps {
  selectedBrandId: string | null;
  onBrandChange: (brandId: string | null) => void;
  isLocked?: boolean;
  lockedMessage?: string;
  showHeader?: boolean;
}

export function BrandContextSection({
  selectedBrandId,
  onBrandChange,
  isLocked = false,
  lockedMessage,
  showHeader = true,
}: BrandContextSectionProps) {
  const { t } = useTranslation();
  const resolvedLockedMessage = lockedMessage ?? t('generation.brandLocked');
  const [isExpanded, setIsExpanded] = useState(false);

  const { data: brand, isLoading: isBrandLoading } = useBrand(selectedBrandId || '');
  const { data: brandAssets, isLoading: isAssetsLoading } = useBrandAssets(selectedBrandId || '');

  return (
    <div className="space-y-4 border-t border-neutral-200 dark:border-neutral-800 pt-4">
      {/* Header */}
      {showHeader && (
        <div className="flex items-center justify-between">
          <h4 className="text-sm font-semibold text-neutral-900 dark:text-neutral-100">
            {t('generation.brandContextSection')}
          </h4>
          {isLocked && (
            <div className="flex items-center gap-1 text-xs text-neutral-500">
              <Lock className="h-3 w-3" />
              <span>{t('generation.brandContextSection')}</span>
            </div>
          )}
        </div>
      )}

      {/* Brand Selection or Locked Message */}
      {isLocked ? (
        <div className="text-sm text-neutral-600 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 rounded-lg p-3">
          {resolvedLockedMessage}
        </div>
      ) : (
        <BrandSelector
          value={selectedBrandId}
          onChange={onBrandChange}
          label={showHeader ? '' : t('generation.brandContextSection')}
          helpText={t('generation.brandHelp')}
        />
      )}

      {/* Brand Details (if brand selected) */}
      {selectedBrandId && (
        <div className="space-y-4">
          {isBrandLoading || isAssetsLoading ? (
            <div className="flex items-center justify-center py-8">
              <Loader2 className="h-6 w-6 animate-spin text-orange-500" />
            </div>
          ) : brand && brandAssets ? (
            <>
              {/* Brand Assets (Logos + Style Images) */}
              {(() => {
                // Collect all non-empty slots
                const allSlots = [
                  ...(brandAssets.logo_slots?.filter(s => s.asset_id) || []),
                  ...(brandAssets.style_image_slots?.filter(s => s.asset_id) || [])
                ];

                return allSlots.length > 0 ? (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300">
                      <ImageIcon className="h-4 w-4" />
                      Brand Assets ({allSlots.length})
                    </div>
                    <div className="flex gap-3 overflow-x-auto p-1">
                      {allSlots.map((slot, idx) => (
                        <div
                          key={idx}
                          className="flex-shrink-0 w-20 h-20 rounded-lg bg-neutral-100 dark:bg-neutral-800 overflow-hidden"
                        >
                          <img
                            src={getAssetUrl(slot.asset_id!, 'thumb')}
                            alt={slot.asset_filename || `Brand asset ${idx + 1}`}
                            className="w-full h-full object-contain p-2"
                            loading="lazy"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                ) : null;
              })()}

              {/* Brand Colors */}
              {brand.brand_colors && brand.brand_colors.length > 0 && (
                <div className="space-y-2">
                  <div className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300">
                    <Palette className="h-4 w-4" />
                    Brand Colors
                  </div>
                  <div className="flex gap-2">
                    {brand.brand_colors.map((color, idx) => (
                      <div
                        key={idx}
                        className="w-10 h-10 rounded-lg border border-neutral-300 dark:border-neutral-700 shadow-sm"
                        style={{ backgroundColor: color }}
                        title={color}
                      />
                    ))}
                  </div>
                </div>
              )}

              {/* AI Description (Collapsible) */}
              {brand.ai_description && (
                <div className="space-y-2">
                  <button
                    onClick={() => setIsExpanded(!isExpanded)}
                    className="flex items-center gap-2 text-sm font-medium text-neutral-700 dark:text-neutral-300 hover:text-neutral-900 dark:hover:text-neutral-100 transition-colors w-full"
                  >
                    {isExpanded ? (
                      <ChevronDown className="h-4 w-4" />
                    ) : (
                      <ChevronRight className="h-4 w-4" />
                    )}
                    AI Brand Description
                  </button>

                  {isExpanded && (
                    <div className="text-sm text-neutral-600 dark:text-neutral-400 bg-neutral-100 dark:bg-neutral-800 rounded-lg p-3 leading-relaxed">
                      {brand.ai_description}
                    </div>
                  )}
                </div>
              )}
            </>
          ) : null}
        </div>
      )}
    </div>
  );
}
